import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { CheckCircleOutline, FactCheckOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import financeService from '../../../services/financeService';
import { ManualJournal, ManualJournalStatus } from '../../../types/finance';
import { useAuth } from '../../../context/AuthContext';
import { useApprovalRule } from '../../../hooks/useApprovalRule';
import { useTimezone } from '../../../hooks/useTimezone';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { useCurrencyFormat } from '../../../hooks/useCurrencyFormat';
import EmptyState from '../../common/EmptyState';
import ReasonDialog from '../../common/ReasonDialog';
import JournalLinesTable from '../ledger/JournalLinesTable';

/**
 * Manual journals awaiting a second person, and those sent back. The maker
 * sees theirs here too; only someone else can approve.
 */
const ManualJournalsPanel: React.FC<{ refreshKey: number; onDecided: () => void; onCount?: (n: number) => void }> = ({
  refreshKey, onDecided, onCount,
}) => {
  const { decodedToken } = useAuth();
  const { ownBlocked } = useApprovalRule();
  const { formatDate, formatDateTime } = useTimezone();
  const { canWrite } = useModulePermissions();
  const money = useCurrencyFormat();
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState<ManualJournalStatus>('PENDING');
  const [items, setItems] = useState<ManualJournal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<ManualJournal | null>(null);
  const canApprove = canWrite('finance_journal_approve');

  const load = useCallback(async () => {
    try {
      const res = await financeService.listManualJournals(status, 1, 50);
      setItems(res.items);
      if (status === 'PENDING') onCount?.(res.pagination.totalItems);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load manual journals.');
    }
  }, [status, onCount]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const approve = async (j: ManualJournal) => {
    setBusyId(j._id);
    try {
      const res = await financeService.approveManualJournal(j._id);
      enqueueSnackbar(`Approved and posted as ${res.entryNo}`, { variant: 'success' });
      onDecided();
      await load();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not approve this journal.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box>
      <Tabs value={status} onChange={(_, v) => setStatus(v)} sx={{ mb: 2, minHeight: 36, '& .MuiTab-root': { textTransform: 'none', minHeight: 36 } }}>
        <Tab value="PENDING" label="Waiting for approval" />
        <Tab value="REJECTED" label="Sent back" />
      </Tabs>

      {error && <Alert severity="error">{error}</Alert>}
      {items && items.length === 0 && (
        <EmptyState
          icon={status === 'PENDING' ? <CheckCircleOutline /> : <FactCheckOutlined />}
          title={status === 'PENDING' ? 'Nothing waiting' : 'Nothing sent back'}
          description={status === 'PENDING' ? 'Manual journals that need a second person appear here.' : 'Rejected journals appear here with the reason.'}
          dense
        />
      )}

      <Stack spacing={2}>
        {items?.map(j => {
          const own = ownBlocked(j.createdBy === decodedToken?.userId);
          return (
            <Paper key={j._id} variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ px: 2.5, py: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={700}>{j.memo}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Dated {formatDate(j.entryDate)} · entered by {j.createdByName ?? 'Unknown'} {formatDateTime(j.createdAt)}
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  {money(j.totalPaisa / 100)}
                </Typography>
              </Stack>
              <Box sx={{ borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
                <JournalLinesTable lines={j.lines} />
              </Box>
              {status === 'REJECTED' ? (
                <Alert severity="warning" sx={{ borderRadius: 0 }}>
                  Sent back by {j.decidedByName ?? 'Unknown'}: {j.rejectionReason}
                </Alert>
              ) : canApprove && (
                <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ px: 2.5, py: 1.5 }}>
                  <Button color="inherit" onClick={() => setRejecting(j)} disabled={busyId === j._id}>Send back</Button>
                  <Tooltip title={own ? 'You entered this journal — someone else must approve it.' : ''}>
                    <span>
                      <Button variant="contained" disableElevation onClick={() => approve(j)} disabled={own || busyId === j._id}>
                        {busyId === j._id ? 'Posting…' : 'Approve and post'}
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              )}
            </Paper>
          );
        })}
      </Stack>

      <ReasonDialog
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title="Send this journal back?"
        consequence="It won't post. Its maker sees your reason and can enter a corrected journal."
        facts={rejecting ? [{ label: 'Journal', value: rejecting.memo }, { label: 'Amount', value: money(rejecting.totalPaisa / 100) }] : []}
        confirmText="Send back"
        confirmColor="warning"
        reasonPlaceholder="What needs to change?"
        onConfirm={async reason => {
          await financeService.rejectManualJournal(rejecting!._id, reason);
          enqueueSnackbar('Journal sent back', { variant: 'success' });
          await load();
        }}
      />
    </Box>
  );
};

export default ManualJournalsPanel;
